import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Rating } from "primereact/rating";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputSwitch } from "primereact/inputswitch";
import { supportApi } from "../../services";
import { toast } from "react-toastify";

const ROLE_OPT = ["Agent", "Senior Agent", "Team Lead", "Manager"].map((v) => ({
  label: v,
  value: v,
}));

const empty = {
  userId: null,
  role: null,
  department: null,
  categories: [],
  isActive: true,
  displayName: "",
};

const AgentMgmt = ({ departmentId }) => {
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmAgent, setConfirmAgent] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      supportApi.getAgents(),
      supportApi.getCategories(),
      supportApi.getUsers(),
      supportApi.getDepartments(),
    ])
      .then(([agRes, catRes, usrRes, deptRes]) => {
        setAgents(Array.isArray(agRes.data?.data) ? agRes.data.data : []);
        setCategories(
          (Array.isArray(catRes.data?.data) ? catRes.data.data : []).map(
            (c) => ({ label: c.name, value: c.name }),
          ),
        );
        const usrList = Array.isArray(usrRes.data?.data)
          ? usrRes.data.data
          : [];
        setUsers(
          usrList.map((u) => ({
            label:
              `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() +
              (u.email ? ` (${u.email})` : ""),
            value: u.id,
          })),
        );
        setDepartments(
          (Array.isArray(deptRes.data?.data) ? deptRes.data.data : []).map(
            (d) => ({ label: d.title, value: d.id }),
          ),
        );
      })
      .catch(() => toast.error("Failed to load agents"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openNew = () => {
    setForm(empty);
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      userId: row.userId,
      role: row.role,
      department: row.department,
      departmentId: row.departmentId ?? null,
      categories: row.categories ?? [],
      isActive: row.isActive,
      displayName: Array.isArray(row.displayName)
        ? (row.displayName[0] ?? null)
        : (row.displayName ?? null),
    });
    setDialog(true);
  };

  const onSave = async () => {
    if (!form.userId || !form.role || !form.departmentId) {
      toast.warn("User, Role and Department are required");
      return;
    }
    if (!form.displayName?.trim()) {
      toast.warn("Display Name is required");
      return;
    }
    const isDuplicate = agents.some((a) => {
      const dn = Array.isArray(a.displayName)
        ? a.displayName[0]
        : a.displayName;
      return dn === form.displayName.trim() && a.id !== form.id;
    });
    if (isDuplicate) {
      toast.warn("Display Name already exists, must be unique");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      displayName: form.displayName ? [form.displayName] : [],
      department: departments.find((d) => d.value === form.departmentId)?.label ?? form.department,
    };
    try {
      if (form.id) {
        await supportApi.updateAgent(form.id, payload);
        toast.success("Agent updated");
      } else {
        await supportApi.createAgent(payload);
        toast.success("Agent added");
      }
      setDialog(false);
      load();
    } catch {
      toast.error("Failed to save agent");
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = async (agent) => {
    if (agent.isActive) {
      setConfirmAgent(agent);
      return;
    }
    try {
      await supportApi.updateAgent(agent.id, { isActive: true });
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const onConfirmDeactivate = async () => {
    try {
      await supportApi.updateAgent(confirmAgent.id, { isActive: false });
      setConfirmAgent(null);
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deptIds = departmentId ? String(departmentId).split(",").map(Number).filter(Boolean) : [];
  const filtered = agents.filter((a) => {
    const matchSearch =
      !search ||
      a.user?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      (a.departmentInfo?.title ?? a.department ?? "").toLowerCase().includes(search.toLowerCase());
    const matchDept = !deptIds.length || deptIds.includes(a.departmentId);
    return matchSearch && matchDept;
  });

  const agentName = (a) => {
    const name =
      `${a.user?.firstName ?? ""} ${a.user?.lastName ?? ""}`.trim() || "—";
    const dn = Array.isArray(a.displayName) ? a.displayName[0] : a.displayName;
    return { name, dn };
  };
  const initials = (a) => agentName(a).name.substring(0, 2).toUpperCase();
  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <div className="search-wrap">
          <i className="pi pi-search" />
          <InputText
            placeholder="Search agents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
        </div>
        <Button
          label="Add Agent"
          icon="pi pi-plus"
          size="small"
          style={{
            marginLeft: "auto",
            background: "var(--blue)",
            border: "none",
          }}
          onClick={openNew}
        />
      </div>

      <DataTable value={filtered} loading={loading} stripedRows>
        <Column
          header="Agent"
          body={(a) => {
            const { name, dn } = agentName(a);
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar
                  label={name.substring(0, 2).toUpperCase()}
                  shape="circle"
                  style={{
                    background: "var(--blue)",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                />
                <div>
                  {dn && (
                    <div style={{ fontWeight: 700, fontSize: 12.5 }}>{dn}</div>
                  )}
                  <div
                    style={{
                      fontSize: dn ? 11 : 12.5,
                      fontWeight: dn ? 400 : 700,
                      color: dn ? "var(--muted)" : undefined,
                    }}
                  >
                    {name}
                  </div>
                  {a.role && (
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      Role: {a.role}
                    </div>
                  )}
                </div>
              </div>
            );
          }}
          style={{ minWidth: 180 }}
        />
        <Column
          field="user.email"
          header="Email"
          body={(a) => a.user?.email ?? "—"}
          style={{ fontSize: 12 }}
        />
        <Column
          field="department"
          header="Department"
          body={(a) => a.departmentInfo?.title ?? a.department ?? "—"}
        />
        <Column
          header="Categories"
          body={(a) => (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {(a.categories ?? []).map((c) => (
                <Tag
                  key={c}
                  value={c}
                  className="p-tag tag-open"
                  style={{
                    fontSize: "5px",
                    fontWeight: 200,
                  }}
                />
              ))}
            </div>
          )}
        />
        <Column
          field="openTickets"
          header="Open"
          body={(a) => (
            <strong style={{ color: "var(--orange)" }}>
              {a.openTickets ?? 0}
            </strong>
          )}
          style={{ width: 60, textAlign: "center" }}
        />
        <Column
          field="resolvedMtd"
          header="Resolved (MTD)"
          style={{ textAlign: "center" }}
          body={(a) => a.resolvedMtd ?? 0}
        />
        <Column
          header="CSAT"
          body={(a) => (
            <div>
              <Rating
                value={a.csatRating ?? 0}
                readOnly
                cancel={false}
                style={{ fontSize: 12 }}
              />
              <div style={{ fontSize: 11, color: "var(--green)" }}>
                {a.csat ?? "—"}/5
              </div>
            </div>
          )}
        />
        <Column
          header="Status"
          body={(a) => (
            <Tag
              value={a.isActive ? "Active" : "Inactive"}
              className={`p-tag ${a.isActive ? "tag-resolved" : "tag-closed"}`}
            />
          )}
          style={{ width: 80 }}
        />
        <Column
          header="Actions"
          body={(a) => (
            <div style={{ display: "flex", gap: 4 }}>
              <Button
                label="Edit"
                size="small"
                outlined
                severity="secondary"
                onClick={() => openEdit(a)}
              />
              <Button
                label={a.isActive ? "Deactivate" : "Activate"}
                size="small"
                outlined
                severity={a.isActive ? "danger" : "success"}
                onClick={() => onToggleActive(a)}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header={form.id ? "Edit Agent" : "Add Agent"}
        visible={dialog}
        style={{ width: 480 }}
        onHide={() => setDialog(false)}
        contentStyle={{ overflowY: "auto", maxHeight: "75vh" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              User *
            </label>
            <Dropdown
              value={form.userId}
              options={users}
              onChange={(e) => set("userId", e.value)}
              placeholder="Select user…"
              style={{ width: "100%" }}
              filter
              showClear
              filterPlaceholder="Search user…"
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Display Name *
            </label>
            <InputText
              value={form.displayName ?? ""}
              onChange={(e) => set("displayName", e.target.value)}
              placeholder="Enter display name…"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Role *
            </label>
            <Dropdown
              value={form.role}
              options={ROLE_OPT}
              onChange={(e) => set("role", e.value)}
              placeholder="Select role…"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Department *
            </label>
            <Dropdown
              value={form.departmentId}
              options={departments}
              onChange={(e) => set("departmentId", e.value)}
              placeholder="Select department…"
              style={{ width: "100%" }}
              filter
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
              }}
            >
              Categories
            </label>
            <MultiSelect
              value={form.categories}
              options={categories}
              onChange={(e) => set("categories", e.value)}
              placeholder="Select categories…"
              style={{ width: "100%" }}
              display="chip"
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <label style={{ fontSize: 11.5, fontWeight: 600 }}>Active</label>
            <InputSwitch
              checked={!!form.isActive}
              onChange={(e) => set("isActive", e.value)}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 4,
            }}
          >
            <Button
              label="Cancel"
              outlined
              severity="secondary"
              onClick={() => setDialog(false)}
            />
            <Button
              label="Save"
              icon="pi pi-save"
              loading={saving}
              style={{ background: "var(--blue)", border: "none" }}
              onClick={onSave}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Confirm Deactivate"
        visible={!!confirmAgent}
        style={{ width: 380 }}
        onHide={() => setConfirmAgent(null)}
      >
        <div style={{ fontSize: 13, marginBottom: 20 }}>
          Are you sure you want to deactivate{" "}
          <strong>{confirmAgent ? agentName(confirmAgent).name : ""}</strong>?
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            label="Cancel"
            outlined
            severity="secondary"
            onClick={() => setConfirmAgent(null)}
          />
          <Button
            label="Deactivate"
            severity="danger"
            onClick={onConfirmDeactivate}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default AgentMgmt;
